import { useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import ValidationSummary from '../../../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import { ValidationHelper, type ValidatableComponent } from '../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import PageIntroduction from '../../Shared/PageIntroduction';
import type AddTeacherApiModel from './add-teacher.api-model';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import InformationBox from '../../Shared/InformationBox';
import { Role } from '../../../../models/pa-egna-ben/role.enum';
import './style.scss';
import type ParticipationApiModel from '../../models/participation.api-model';
import NotFoundRedirect from '../NotFoundRedirect';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';

function AddTeacher({ viewerRole }: { viewerRole: Role }) {
  // === PARAMS ===
  const { participationId } = useParams();

  // === STATE ===
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [emailAddressState, setEmailAddressState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [{ httpResponseStatusCode: participationsHttpResponseStatusCode }] = useFetch<ParticipationApiModel>(`/api/paegnaben/${viewerRole}/participations/${participationId}`);

  // === REFS ===
  const emailAddressRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: emailAddressRef.current, validationInfo: emailAddressState.validationInfo, setState: setEmailAddressState },
      { component: phoneNumberRef.current, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;
      setSaveErrorCode(undefined);
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setSaveStatus(Status.loading);
      const addTeacherApiModel: AddTeacherApiModel = {
        emailAddress: emailAddressState.value,
        phoneNumber: phoneNumberState.value,
      };
      const antiForgeryTokenElement = document.getElementById('antiForgeryToken') as HTMLInputElement;
      try {
        const response = await fetch(`/api/paegnaben/${viewerRole}/participations/${participationId}/add-teacher`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': antiForgeryTokenElement.value },
          body: JSON.stringify(addTeacherApiModel),
        });
        if (response.ok) {
          setSaveStatus(Status.success);
          setSaveErrorCode(undefined);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Pedagog tillagd', '');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Pedagog tillagd - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error.status };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
    if (submitResult?.status === Status.failed && (submitResult?.errorCode === 404 || submitResult?.errorCode === 409)) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('information-box-error-container')?.focus();
    }
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === HELPERS ===
  function getSaveErrorHeading(): string {
    if (saveStatus === Status.failed) {
      switch (saveErrorCode) {
        case 409:
          return 'Pedagogen är redan tillagd på klassen';
        case 404:
          return 'Användarkonto saknas';
        default:
          return 'Kunde inte lägga till pedagog';
      }
    }
    return '';
  }
  function getSaveErrorText(): string {
    if (saveStatus === Status.failed) {
      switch (saveErrorCode) {
        case 409:
          return '';
        case 404:
          return 'Det kan bero på att användaren du vill lägga till inte registrerat ett användarkonto eller bekräftat sin e-postadress och loggat in för att aktivera kontot.';
        default:
          return '';
      }
    }
    return '';
  }

  // === RENDER ===
  if (saveStatus === Status.success) {
    return <Navigate to={`/klasser/${participationId}/klassuppgifter`} />;
  }
  if ((saveStatus === Status.failed && saveErrorCode === 401) || participationsHttpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if ((saveStatus === Status.failed && saveErrorCode === 403) || participationsHttpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (participationsHttpResponseStatusCode === 404) {
    return <NotFoundRedirect />;
  }

  return (
    <div>
      <PageIntroduction heading="Lägg till pedagog" requiredText={false} />
      <InformationBox
        type={InformationBoxType.Default}
        textElement={
          <>
            <h2 className="h3">Så här gör du</h2>
            <ol className="add-teacher__information-box-list">
              <li>Pedagogen du lägger till behöver först registrera ett användarkonto</li>
              <li>Ange pedagogens registrerade e-postadress och klicka på &quot;Lägg till Pedagog&quot;</li>
            </ol>
            <span>Nu kan pedagogen själv logga in, ändra klassuppgifter och rapportera in poäng.</span>
          </>
        }
        className="mb-4 mb-md-5"
      />
      <form id="form">
        <fieldset>
          <legend className="visually-hidden">Formulär för att lägga till pedagog</legend>
          <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
          <div id="information-box-error-container" tabIndex={-1}>
            {saveStatus === Status.failed && (saveErrorCode === 404 || saveErrorCode === 409 || saveErrorCode === 400) && (
              <InformationBox type={InformationBoxType.Error} headingElement={<span className="h3">{getSaveErrorHeading()}</span>} textElement={getSaveErrorText()} className="mb-3" />
            )}
          </div>
          <TextFieldFormGroup
            ref={emailAddressRef}
            labelText="E-postadress"
            required={true}
            inputId="email-input"
            type="email"
            serverValidationName="emailAddress"
            state={[emailAddressState, setEmailAddressState]}
            hideRequiredAsterisk={true}
          />
          <TextFieldFormGroup
            ref={phoneNumberRef}
            labelText="Telefonnummer (valfritt)"
            required={false}
            inputId="phonenumber-input"
            type="text"
            serverValidationName="phoneNumber"
            state={[phoneNumberState, setPhoneNumberState]}
          />
          <div className="d-grid d-md-flex gap-3">
            <Link to={`/klasser/${participationId}/klassuppgifter`} className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1">
              Avbryt
            </Link>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleSaveButtonClick}>
              Lägg till pedagog
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddTeacher;
